// ============================================================
// Login — GitHub-only OLIVIUM trust entry.
//
// Square Code is a governed private operator system. The only
// login method is GitHub OAuth. There is no public signup, no
// email/password form, and no casual entry. After GitHub auth,
// the user hits the allowlist gate before entering the shell.
// ============================================================

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Banner } from '@square/ui'
import { signInWithGitHub, getBrowserAuthReadiness } from '@square/core'
import { useAuth } from '../auth/AuthContext'
import { sanitizeAuthNext, stashAuthNext } from '../auth/redirect'

export default function Login() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = sanitizeAuthNext(params.get('next'))

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [phase, setPhase] = useState(0)

  // Deployment auth readiness. The browser Supabase config is inlined at
  // build time; when it is missing, GitHub sign-in can only fail with
  // "Supabase client not available". Show an honest, non-secret state and
  // disable the button rather than letting the operator click into an error.
  const authReady = getBrowserAuthReadiness()

  // Animated entry sequence
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 200)
    const t2 = setTimeout(() => setPhase(2), 600)
    const t3 = setTimeout(() => setPhase(3), 1000)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [])

  useEffect(() => {
    if (!user) return
    navigate(next, { replace: true })
  }, [user, next, navigate])

  if (user) return null

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--olv-void)',
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient glow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse at 30% 20%, rgba(124,92,252,0.06) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(0,212,255,0.03) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 28,
          width: 400,
          maxWidth: '100%',
        }}
      >
        {/* OLIVIUM brand mark */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            opacity: phase >= 1 ? 1 : 0,
            transform: phase >= 1 ? 'translateY(0)' : 'translateY(-12px)',
            transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(124,92,252,0.15), rgba(0,212,255,0.08))',
              border: '1px solid rgba(124,92,252,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              color: '#7c5cfc',
              fontFamily: 'var(--font-brand)',
              boxShadow: '0 0 32px rgba(124,92,252,0.2)',
            }}
          >
            ◈
          </div>
          <div
            style={{
              fontFamily: 'var(--font-brand)',
              fontSize: 18,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #7c5cfc, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.08em',
            }}
          >
            OLIVIUM
          </div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--olv-text-tertiary)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            Trust Operating Layer
          </div>
        </div>

        {/* Login card */}
        <div
          style={{
            width: '100%',
            background: 'var(--olv-elevated)',
            border: '1px solid var(--olv-panel-border)',
            borderRadius: 16,
            padding: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,92,252,0.06)',
            opacity: phase >= 2 ? 1 : 0,
            transform: phase >= 2 ? 'translateY(0)' : 'translateY(8px)',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--font-brand)',
                fontSize: 22,
                color: 'var(--olv-text-primary)',
                marginBottom: 6,
              }}
            >
              Operator access
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--olv-text-tertiary)',
                fontFamily: 'var(--font-mono)',
                lineHeight: 1.7,
              }}
            >
              Square Code is a governed private operator system. Access is
              by invitation only. Authenticate with your approved GitHub
              identity to enter.
            </div>
          </div>

          {error && <Banner severity="critical" title="Authentication failed">{error}</Banner>}

          {/* GitHub sign-in — primary and only method */}
          <button
            type="button"
            onClick={async () => {
              if (!authReady.ready) return
              setBusy(true)
              setError(null)
              // Persist the intended destination so it survives the OAuth
              // round-trip even if the provider drops the ?next= query param.
              stashAuthNext(next)
              const result = await signInWithGitHub(next)
              setBusy(false)
              if (!result.ok) setError(result.error || 'GitHub authentication failed')
            }}
            disabled={busy || !authReady.ready}
            style={{
              width: '100%',
              padding: '12px 0',
              borderRadius: 10,
              border: '1px solid rgba(124,92,252,0.3)',
              background: 'linear-gradient(135deg, rgba(124,92,252,0.12), rgba(0,212,255,0.06))',
              color: 'var(--olv-text-primary)',
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.04em',
              cursor: !authReady.ready ? 'not-allowed' : busy ? 'wait' : 'pointer',
              opacity: !authReady.ready ? 0.5 : busy ? 0.6 : 1,
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            {!authReady.ready
              ? 'Authentication unavailable'
              : busy
                ? 'Redirecting to GitHub…'
                : 'Continue with GitHub'}
          </button>

          {/* Announce the busy transition to screen readers — the
              button's own label change is not read out. */}
          <div
            role="status"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {busy ? 'Redirecting to GitHub' : ''}
          </div>

          {authReady.ready ? (
            <div
              style={{
                fontSize: 10,
                color: 'var(--olv-text-tertiary)',
                fontFamily: 'var(--font-mono)',
                textAlign: 'center',
                lineHeight: 1.6,
                opacity: 0.7,
              }}
            >
              Only approved GitHub identities will be admitted.
              Unauthorized attempts are logged.
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 14px',
                borderRadius: 10,
                background: 'rgba(255,159,10,0.06)',
                border: '1px solid rgba(255,159,10,0.28)',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: '#ffb84d',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                }}
              >
                Operator authentication is not configured for this deployment
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--olv-text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  lineHeight: 1.7,
                }}
              >
                This deployment was built without the browser Supabase configuration, so
                GitHub sign-in cannot start. This is a deployment configuration issue — not a
                problem with your account. An operator must configure the deployment and rebuild.
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: 'var(--olv-text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  opacity: 0.7,
                  letterSpacing: '0.06em',
                }}
              >
                env: {authReady.appEnv} · mode: {authReady.oliviumMode}
              </div>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  alignSelf: 'flex-start',
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--olv-panel-border)',
                  background: 'transparent',
                  color: 'var(--olv-text-secondary)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          )}
        </div>

        {/* Trust footer */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            opacity: phase >= 3 ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 9,
              color: 'var(--olv-text-tertiary)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.08em',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#059669', display: 'inline-block' }} />
              GITHUB VERIFIED
            </span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#059669', display: 'inline-block' }} />
              AUDIT LOGGED
            </span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#059669', display: 'inline-block' }} />
              ALLOWLIST GATED
            </span>
          </div>
          <div style={{ fontSize: 8, color: 'var(--olv-text-tertiary)', fontFamily: 'var(--font-mono)', opacity: 0.5, letterSpacing: '0.14em' }}>
            OLIVIUM TRUST LAYER · SQUARE CODE
          </div>
        </div>
      </div>
    </div>
  )
}
